import React, { ReactElement, ReactNode } from 'react';
import { Box, Breakpoint, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface BaseDialogProps {
    onClose: () => void;
    open?: boolean;
    title: string;
    subtitle?: string;
    titleIcon?: ReactElement;
    titleColor?: string;
    disableClose?: boolean;
    hideClose?: boolean;
    cancelLabel?: string;
    actionLabel?: string;
    onAction?: () => void;
    actionDisabled?: boolean;
    actionsLeft?: ReactElement;
    actions?: ReactElement;
    maxWidth?: Breakpoint;
    fullWidth?: boolean;
    children: ReactNode;
}

export function BaseDialog({
    onClose,
    open = true,
    title,
    subtitle,
    titleIcon,
    titleColor,
    disableClose = false,
    hideClose = false,
    cancelLabel,
    actionLabel,
    onAction,
    actionDisabled,
    actionsLeft,
    actions,
    maxWidth = 'sm',
    fullWidth = true,
    children,
}: BaseDialogProps) {
    const simpleActions = (cancelLabel || actionLabel) ? (
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'center', gap: '16%' }}>
            {cancelLabel && (
                <Button variant="outlined" onClick={onClose} sx={{ minWidth: 100 }}>
                    {cancelLabel}
                </Button>
            )}
            {actionLabel && (
                <Button
                    variant="contained"
                    onClick={onAction}
                    disabled={actionDisabled}
                    sx={{ minWidth: 100 }}
                >
                    {actionLabel}
                </Button>
            )}
        </Box>
    ) : undefined;

    const renderedActions = simpleActions ?? actions;

    return (
        <Dialog
            open={open}
            onClose={disableClose ? undefined : onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
        >
            <DialogTitle>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                    <Stack direction="row" spacing={1} alignItems="center">
                        {titleIcon}
                        <Stack>
                            <Box component="span" sx={{ color: titleColor ?? 'inherit' }}>{title}</Box>
                            {subtitle && (
                                <Typography variant="body2" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                    {!hideClose && (
                        <IconButton onClick={onClose} disabled={disableClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </DialogTitle>

            <DialogContent>{children}</DialogContent>

            {(actionsLeft || renderedActions) && (
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: actionsLeft ? 'space-between' : undefined }}>
                    {actionsLeft}
                    {renderedActions}
                </DialogActions>
            )}
        </Dialog>
    );
}
